import type {
  AgentComponent,
  PublishedTemplateVersion,
  PublishTemplateBody,
  UnpublishedTemplateChanges,
} from '@reach/shared-types/agent-components';
import type { AgentTemplateTenantCount } from '@reach/shared-types/agent-templates';
import type { AgentTemplateKind } from '@reach/shared-types/agents';
import { partnerApi, type ApiError, type ApiResponse } from '../../lib/api';

const BASE = '/partner/agent-templates';

export const templateComponentKeys = {
  all: ['agent-template-components'] as const,
  list: (templateId: string) => [...templateComponentKeys.all, templateId] as const,
} as const;

export const templateKindsUsingComponentKeys = {
  all: ['agent-template-kinds-using-component'] as const,
  detail: (componentId: string) => [...templateKindsUsingComponentKeys.all, componentId] as const,
} as const;

export const unpublishedChangesKeys = {
  all: ['agent-template-unpublished-changes'] as const,
  detail: (templateId: string) => [...unpublishedChangesKeys.all, templateId] as const,
} as const;

export const templateTenantCountKeys = {
  all: ['agent-template-tenant-count'] as const,
  detail: (templateId: string) => [...templateTenantCountKeys.all, templateId] as const,
} as const;

const componentOnTemplatePath = (templateId: string, componentId: string): string =>
  `${BASE}/${templateId}/components/${componentId}`;

export async function fetchTemplateComponents(templateId: string): Promise<AgentComponent[]> {
  const response = await partnerApi.request<ApiResponse<AgentComponent[]>>(
    `${BASE}/${templateId}/components`,
  );
  return response.data;
}

export async function fetchTemplateTenantCount(templateId: string): Promise<number> {
  const response = await partnerApi.request<ApiResponse<AgentTemplateTenantCount>>(
    `${BASE}/${templateId}/tenant-count`,
  );
  return response.data.tenantCount;
}

export async function addTemplateComponent(templateId: string, componentId: string): Promise<void> {
  await partnerApi.request<void>(componentOnTemplatePath(templateId, componentId), { method: 'PUT' });
}

export async function removeTemplateComponent(templateId: string, componentId: string): Promise<void> {
  await partnerApi.request<void>(componentOnTemplatePath(templateId, componentId), {
    method: 'DELETE',
  });
}

export async function fetchTemplateKindsUsingComponent(
  componentId: string,
): Promise<AgentTemplateKind[]> {
  const response = await partnerApi.request<ApiResponse<AgentTemplateKind[]>>(
    `/partner/agent-components/${componentId}/templates`,
  );
  return response.data;
}

export async function fetchUnpublishedChanges(templateId: string): Promise<UnpublishedTemplateChanges> {
  const response = await partnerApi.request<ApiResponse<UnpublishedTemplateChanges>>(
    `${BASE}/${templateId}/changes`,
  );
  return response.data;
}

export async function publishTemplate(
  templateId: string,
  review: PublishTemplateBody = {},
): Promise<PublishedTemplateVersion> {
  const response = await partnerApi.request<ApiResponse<PublishedTemplateVersion>>(
    `${BASE}/${templateId}/versions`,
    { method: 'POST', body: JSON.stringify(review) },
  );
  return response.data;
}

export const publishProblemsFrom = (error: Error): string[] => {
  const listedProblems = (error as ApiError).response?.data?.errors ?? [];
  const problemMessages = listedProblems.map(problem =>
    typeof problem === 'string' ? problem : problem.message,
  );
  const errorListsProblems = problemMessages.length > 0;
  return errorListsProblems ? problemMessages : [error.message];
};
